import { Form, Popup } from 'devextreme-react';
import { RequiredRule } from 'devextreme-react/data-grid';
import { ButtonItem, GroupItem, Label, SimpleItem } from 'devextreme-react/form';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { getItemApi, getListApi } from '../../apiUtils';
import '../../assets/bulk-modal.scss';
import { useAssetClass } from '../../contexts/assetClass';
import { useLoading } from '../../contexts/loadingContext';
import { useTranslation } from '../../contexts/translation';
import { CapacityCategory } from '../../types/capacityCategory.types';
import { DataGridActionType } from '../../types/dataGridActionTypes';
import { StepGroup } from '../../types/stepGroup.types';
import { API_URL } from '../../utils/apiUrl';
import { displayDateFormat_CZ, displayDateFormat_EN } from '../../utils/dates.types';
import './EditCreateModal.scss';
import './TasksBulkEditModal.scss';

type Props = {
  data: any;
  stepGroupList?: StepGroup[];
  isVisible: boolean;
  mutate?: () => void;
  onClose: () => void;
  onSubmit: (data: any, actionType: DataGridActionType) => void;
  actionType: DataGridActionType;
  transFormName: string;
  assetCycleId: number;
};

/**
 * Modalová komponent pro vytváření/editaci záznamů typu Asset cycle step v datagridu
 */
export default function AssetCycleStepGroupModalDate({
  isVisible,
  data,
  stepGroupList,
  actionType,
  onClose,
  onSubmit,
  transFormName,
  assetCycleId,
}: Props) {
  const { assetClass } = useAssetClass();
  const { translate, lang } = useTranslation();
  const formRef = useRef<Form>(null);
  const { loading } = useLoading();
  const [selectedUnit, setSelectedUnit] = useState<string>();
  const [formData, setFormData] = useState<any>({
    ...data,
    ...(actionType === DataGridActionType.Insert && { visible: true }),
  });
  const [estimatedOccurrance, setEstimatedOccurrance] = useState<string | null>(
    translate!('Loading', ''),
  );
  const [isCounterVisible, setCounterVisible] = useState<boolean>(true);
  const [capacityCategoryId, setCapacityCategoryId] = useState<number | null>();
  const [capacityCategories, setCapacityCategories] = useState<CapacityCategory[] | null>();
  const dateFormat = lang === 'en-US' ? displayDateFormat_EN : displayDateFormat_CZ;

  useEffect(() => {
    setFormData({
      ...data,
      ...(actionType === DataGridActionType.Insert && { visible: true }),
      frequency: data && data.frequency ? data.frequency : 1,
      counter: data && data.counter ? data.counter : 0,
    });
    getCapacityCategories();
  }, []);

  useEffect(() => {
    if (formData.frequency === undefined || formData.counter === undefined) return;
    getEstimatedOccurrence();
    handleCounterVisibility();
  }, [formData]);

  const getEstimatedOccurrence = async () => {
    setEstimatedOccurrance('Loading');
    getItemApi({
      url: API_URL.ASSET_CYCLE_CALCULATE_ESTIMATED_OCCURRANCE,
      params: {
        assetCycleId: assetCycleId,
        frequency: formData.frequency,
        counter: formData.counter,
      },
      callAfterSuccess: (result) => {
        setEstimatedOccurrance(moment(result).format(dateFormat));
      },
    });
  };

  const handleCounterVisibility = (): boolean => {
    // Skryjeme counter, pokud je měsíční frekvence
    const isFrequencyOne = formData.frequency === 1;
    setCounterVisible(!isFrequencyOne);
    return isFrequencyOne;
  };

  const onFrequencyChanged = () => {
    getEstimatedOccurrence();
    const isFrequencyOne = handleCounterVisibility();
    if (isFrequencyOne)
      setFormData({
        ...formData,
        counter: 0,
      });
  };

  const onCounterChanged = () => {
    getEstimatedOccurrence();
  };

  const getCapacityCategories = async () => {
    getListApi({
      url: `${API_URL.CAPACITY_CATEGORY_LIST}?parentId=${assetClass}`,
      params: {},
      callAfterSuccess: (result) => {
        setCapacityCategories(result);
        getAssetCycle();
      },
    });
  };

  const getAssetCycle = async () => {
    getListApi({
      url: `${API_URL.ASSET_CYCLE_ITEM}?id=${assetCycleId}`,
      params: {},
      callAfterSuccess: (result) => {
        setCapacityCategoryId(result.capacityCategoryId);
      },
    });
  };

  return (
    <Popup
      visible={isVisible}
      dragEnabled={false}
      closeOnOutsideClick
      onHiding={onClose}
      showTitle
      title={translate!('Asset cycle step group', transFormName)}
      width={'600px'}
      height={'auto'}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData, actionType);
        }}
      >
        <Form
          formData={formData}
          showColonAfterLabel={true}
          showValidationSummary={true}
          ref={formRef}
        >
          {actionType !== DataGridActionType.Update ? (
            <GroupItem>
              <SimpleItem
                dataField="capacityCategoryId"
                editorType="dxSelectBox"
                editorOptions={{
                  items: capacityCategories,
                  valueExpr: 'id',
                  displayExpr: 'name',
                  showClearButton: true,
                  value: capacityCategoryId,
                  onValueChanged: (e: any) => {
                    setFormData({ ...formData, stepGroupId: undefined });
                    setCapacityCategoryId(e.value);
                  },
                }}
              >
                <Label text={translate!('Capacity category', transFormName)} />
              </SimpleItem>
              <SimpleItem
                dataField="stepGroupId"
                editorType="dxSelectBox"
                editorOptions={{
                  items: capacityCategoryId
                    ? stepGroupList?.filter((x) => x.capacityCategoryId === capacityCategoryId)
                    : stepGroupList,
                  valueExpr: 'id',
                  displayExpr: 'name',
                }}
              >
                <Label text={translate!('Step group', transFormName)} />
                <RequiredRule message={translate!('Select step group', transFormName)} />
              </SimpleItem>
            </GroupItem>
          ) : (
            <GroupItem>
              <SimpleItem
                dataField="stepGroupName"
                editorType="dxTextBox"
                editorOptions={{
                  readOnly: true,
                }}
              >
                <Label text={translate!('Step group', transFormName)} />
              </SimpleItem>
            </GroupItem>
          )}
          <GroupItem>
            <SimpleItem
              dataField="frequency"
              editorType="dxSelectBox"
              editorOptions={{
                items: [
                  { value: 1, name: translate!('Monthly', 'Asset.CycleStep') },
                  { value: 3, name: translate!('Quarterly', 'Asset.CycleStep') },
                  { value: 6, name: translate!('Semi-annually', 'Asset.CycleStep') },
                  { value: 12, name: translate!('Annually', 'Asset.CycleStep') },
                ],
                value: formData?.frequency,
                valueExpr: 'value',
                displayExpr: 'name',
                onValueChanged: () => onFrequencyChanged(),
              }}
            >
              <Label text={translate!('Frequency', transFormName)} />
              <RequiredRule message={translate!('Select frequency', transFormName)} />
            </SimpleItem>
          </GroupItem>
          {isCounterVisible && (
            <GroupItem>
              <SimpleItem
                dataField="counter"
                editorType="dxNumberBox"
                editorOptions={{
                  onValueChanged: () => onCounterChanged(),
                }}
              >
                <Label text={translate!('Counter', transFormName)} />
              </SimpleItem>
            </GroupItem>
          )}
          <GroupItem>
            {translate!('Estimated occurrance:', transFormName)}{' '}
            <strong>{estimatedOccurrance}</strong>
          </GroupItem>
          <GroupItem>{translate!('Frequency and counter note', 'Asset.CycleStep')}</GroupItem>
          <GroupItem cssClass="buttons-group" colCountByScreen={{ xs: 2, sm: 2, md: 2, lg: 2 }}>
            <ButtonItem
              buttonOptions={{
                type: 'success',
                text: translate!('Save', ''),
                width: 120,
                useSubmitBehavior: true,
              }}
            />

            <ButtonItem
              buttonOptions={{
                type: 'normal',
                text: translate!('Close', ''),
                onclick: () => onClose(),
                width: 120,
              }}
            />
          </GroupItem>
        </Form>
      </form>
    </Popup>
  );
}
